<template>
  <div class="invite-view">
    <div class="invite-card">
      <h1>You've been invited</h1>

      <div v-if="loading" class="state">Looking up the invitation…</div>

      <div v-else-if="error" class="state error">
        <p>{{ error }}</p>
        <router-link to="/" class="link">Go to home</router-link>
      </div>

      <div v-else-if="status === 'accepted' && invitation" class="state success">
        <p>
          Welcome to <strong>{{ invitation.entityName }}</strong> as
          <span class="role-badge">{{ invitation.role }}</span>.
        </p>
        <p>Heading to your dashboard…</p>
      </div>

      <div v-else-if="invitation" class="invite-body">
        <p class="lead">
          You've been invited to join
          <strong>{{ invitation.entityName }}</strong>
          <template v-if="invitation.entityType === 'tenant' && invitation.parentOrgName">
            (a Tenant under <strong>{{ invitation.parentOrgName }}</strong>)
          </template>
          as <span class="role-badge">{{ invitation.role }}</span>.
        </p>
        <p class="invitee-email">Sign in or sign up with <strong>{{ invitation.email }}</strong> to accept.</p>

        <div v-if="!authStore.isAuthenticated" class="actions">
          <button type="button" class="primary-btn" @click="goToAuth">Sign in / Sign up</button>
        </div>

        <div v-else-if="emailMismatch" class="mismatch">
          <p>
            You're signed in as <strong>{{ authStore.userDetails.email }}</strong>, but this invitation
            is for <strong>{{ invitation.email }}</strong>. Sign out and sign back in with the right email
            to accept.
          </p>
          <button type="button" class="secondary-btn" @click="signOutForReauth">Sign out</button>
        </div>

        <div v-else class="actions">
          <button type="button" class="primary-btn" :disabled="accepting" @click="accept">
            {{ accepting ? 'Accepting…' : 'Accept invitation' }}
          </button>
        </div>
      </div>

      <div v-else class="state">
        <p>This invitation can't be loaded. Sign in first to view it.</p>
        <button type="button" class="primary-btn" @click="goToAuth">Sign in / Sign up</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth.store'
import { profileService, type InvitationPreview } from '@/services/profile'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(true)
const error = ref('')
const invitation = ref<InvitationPreview | null>(null)
const accepting = ref(false)
const status = ref<'idle' | 'accepted'>('idle')

const inviteId = computed(() => String(route.params.id || ''))

const emailMismatch = computed(() => {
  if (!authStore.isAuthenticated || !invitation.value) return false
  const callerEmail = (authStore.userDetails.email || '').toLowerCase().trim()
  return callerEmail !== invitation.value.email
})

const persistInviteIdForPostSignup = () => {
  if (inviteId.value) {
    try { localStorage.setItem('pendingInviteId', inviteId.value) } catch { /* */ }
  }
}

const goToAuth = () => {
  persistInviteIdForPostSignup()
  router.push('/')
}

const signOutForReauth = async () => {
  await authStore.logout()
  persistInviteIdForPostSignup()
}

const accept = async () => {
  if (!invitation.value) return
  accepting.value = true
  error.value = ''
  try {
    await profileService.acceptInvitation(invitation.value.id)
    status.value = 'accepted'
    try { localStorage.removeItem('pendingInviteId') } catch { /* */ }
    setTimeout(() => router.push('/dashboard'), 1500)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { error?: string } }, message?: string }
    error.value = err?.response?.data?.error || err?.message || 'Failed to accept invitation'
  } finally {
    accepting.value = false
  }
}

onMounted(async () => {
  if (!inviteId.value) {
    error.value = 'Missing invitation id'
    loading.value = false
    return
  }
  if (!authStore.isAuthenticated) {
    try { await authStore.checkAuth() } catch { /* */ }
  }
  if (!authStore.isAuthenticated) {
    persistInviteIdForPostSignup()
    loading.value = false
    return
  }
  try {
    invitation.value = await profileService.getInvitation(inviteId.value)
    loading.value = false
    if (!emailMismatch.value && invitation.value.status === 'pending') {
      await accept()
    }
  } catch (e: unknown) {
    const err = e as { response?: { data?: { error?: string } }, message?: string }
    error.value = err?.response?.data?.error || err?.message || 'Could not load invitation'
    loading.value = false
  }
})
</script>

<style scoped>
.invite-view {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  min-height: 70vh;
}

.invite-card {
  width: 100%;
  max-width: 560px;
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: var(--base-layout-form-panel-border-radius);
  padding: clamp(1.5rem, 3vw, 2.5rem);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
  color: var(--color-text);
}

h1 {
  margin: 0 0 1rem;
  color: var(--color-heading);
  font-size: 1.6rem;
}

.lead {
  font-size: 1.05rem;
  line-height: 1.5;
}

.invitee-email {
  margin-top: 0.5rem;
  opacity: 0.8;
  font-size: 0.95rem;
}

.actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.primary-btn,
.secondary-btn {
  border-radius: 999px;
  padding: 0.65rem 1.4rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid var(--vt-c-accent);
  font-size: 1rem;
}

.primary-btn {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.primary-btn:hover:not(:disabled) {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.secondary-btn {
  background: transparent;
  color: var(--color-heading);
}

.secondary-btn:hover:not(:disabled) {
  background: rgba(var(--vt-c-accent-rgb), 0.1);
}

.role-badge {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(var(--vt-c-accent-rgb), 0.15);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.4);
  color: var(--vt-c-accent);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.state {
  padding: 1rem 0;
  opacity: 0.85;
}

.state.error {
  color: #fa9696;
}

.state.success {
  color: var(--color-heading);
}

.mismatch {
  background: rgba(250, 150, 150, 0.08);
  border: 1px solid rgba(250, 150, 150, 0.35);
  border-radius: 10px;
  padding: 1rem;
  margin-top: 1.25rem;
  font-size: 0.95rem;
}

.link {
  color: var(--vt-c-accent);
}
</style>
